/* Aba "Resumo" do Plano Alimentar: visão de consulta rápida do(s) plano(s)
   mais recente(s) do paciente. Só leitura — editar continua no Editor.
   Dados: lista de planos vem pronta do index (dietPlansService.getByPatient);
   refeições/suplementos/água de cada plano via dietPlansService.getById,
   buscados sob demanda e guardados enquanto a aba estiver aberta. */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { dietPlansService } from '../../services/api';
import { SERVICES, strategyById, ordenarRefeicoes, nomeRefeicao, mealIcon } from './helpers';
import { Ico } from '../ui/Ico';
import { initials, ageFrom } from './PatientPicker';

/* Cores dos macros = TotalsBar (ui.jsx); pares escuros = design-v0/gen_dark.py */
const MACRO_COLORS = {
  light: { prot: '#7189a6', carb: '#b9d2bd', fat: '#e8c9a8' },
  dark:  { prot: '#9db8d6', carb: '#9fcfaa', fat: '#e8c9a8' },
};

/* Lê body.dark-mode (ThemeContext alterna essa classe). SVG do recharts não é
   coberto pelo dark-mode.css, então a cor do gráfico é escolhida aqui. */
function useBodyDark() {
  const read = () => typeof document !== 'undefined' && document.body.classList.contains('dark-mode');
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(read()));
    obs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/* ── formatação ── */
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const recorded = (v) => v !== null && v !== undefined && String(v).trim() !== '' && Number.isFinite(Number(v));
const nutrientText = (v, unit) => recorded(v) ? `${num(v).toLocaleString('pt-BR')}${unit}` : '—';
const fmtInt = (v) => Math.round(num(v)).toLocaleString('pt-BR');
const isoDay = (p) => String(p?.start_date || p?.created_at || '').slice(0, 10);
const toDate = (iso) => { const d = iso ? new Date(iso + 'T12:00:00') : null; return d && !isNaN(d) ? d : null; };
const fmtDate = (iso, opts = { day: '2-digit', month: 'short', year: 'numeric' }) => {
  const d = toDate(iso);
  return d ? d.toLocaleDateString('pt-BR', opts).replace('.', '') : '—';
};
const relDays = (iso) => {
  const d = toDate(iso);
  if (!d) return '';
  const t = new Date(); t.setHours(12, 0, 0, 0);
  const n = Math.round((t - d) / 864e5);
  if (n <= 0) return 'hoje';
  if (n === 1) return 'ontem';
  if (n < 60) return `há ${n} dias`;
  const m = Math.round(n / 30);
  return m < 24 ? `há ${m} meses` : `há ${Math.round(n / 365)} anos`;
};
const capital = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const macroInfo = (p) => {
  const g = { prot: num(p?.protein_grams), carb: num(p?.carbs_grams), fat: num(p?.fat_grams) };
  const k = { prot: g.prot * 4, carb: g.carb * 4, fat: g.fat * 9 };
  const tot = k.prot + k.carb + k.fat;
  if (!tot) return null;
  const pp = Math.round(k.prot / tot * 100), pc = Math.round(k.carb / tot * 100);
  return { g, k, pct: { prot: pp, carb: pc, fat: 100 - pp - pc } };
};

const sortPlans = (list) => [...(list || [])].sort((a, b) =>
  isoDay(b).localeCompare(isoDay(a))
  || String(b.created_at || '').localeCompare(String(a.created_at || ''))
  || (num(b.id) - num(a.id)));

const parseMeals = (raw) => {
  let meals = raw;
  if (typeof meals === 'string') { try { meals = JSON.parse(meals); } catch { meals = null; } }
  return ordenarRefeicoes(meals).map(([key, m]) => {
    const foods = (Array.isArray(m.alimentos) ? m.alimentos : [])
      .filter(a => a && String(a.alimento || '').trim());
    const s = (f) => foods.reduce((t, a) => t + num(a[f]), 0);
    return {
      key, nome: nomeRefeicao(key, m), horario: m.horario || '', foods,
      kcal: s('calorias'), prot: s('proteina'), carb: s('carboidratos'), fat: s('gordura'),
      hasCalories: foods.length > 0 && foods.every(a => recorded(a.calorias)) && s('calorias') > 0,
    };
  });
};

/* ══════════════════════════════════════════════════════ */
export default function ResumoTab({ patient, plans, status, onRetry, onOpenEditor, onNew, onManual, onModelos, onShowHistory }) {
  const dark   = useBodyDark();
  const colors = dark ? MACRO_COLORS.dark : MACRO_COLORS.light;
  const sorted = useMemo(() => sortPlans(plans), [plans]);
  const [selId, setSelId]     = useState(null);
  const [details, setDetails] = useState({}); // id → { status:'loading'|'ok'|'error', data }

  const idx     = Math.max(0, sorted.findIndex(p => p.id === selId));
  const current = sorted[idx] || null;
  const prev    = sorted[idx + 1] || null;

  const loadDetail = useCallback(async (id) => {
    setDetails(d => ({ ...d, [id]: { status: 'loading' } }));
    try {
      const r = await dietPlansService.getById(id);
      setDetails(d => ({ ...d, [id]: r?.success ? { status: 'ok', data: r.data } : { status: 'error' } }));
    } catch {
      setDetails(d => ({ ...d, [id]: { status: 'error' } }));
    }
  }, []);

  useEffect(() => {
    if (current && !details[current.id]) loadDetail(current.id);
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── cabeçalho do paciente ── */
  const age = ageFrom(patient?.birth_date);
  const sexo = patient?.gender === 'F' ? 'Feminino' : patient?.gender === 'M' ? 'Masculino' : null;
  const countLbl = status !== 'ok' ? null
    : sorted.length === 0 ? 'nenhum plano salvo'
    : `${sorted.length} ${sorted.length > 1 ? 'planos salvos' : 'plano salvo'}`;

  const ctx = (
    <div className="pa-ctx pa-fade">
      <span className="pa-avatar pa-avatar-lg" aria-hidden="true">{initials(patient?.name)}</span>
      <div>
        <div className="pa-ctx-name">{patient?.name || <span className="pa-skel" style={{ display: 'inline-block', width: 200, height: 26 }} />}</div>
        <div className="pa-ctx-meta">{[age !== null ? `${age} anos` : null, sexo, countLbl].filter(Boolean).join(' · ')}</div>
      </div>
      {status === 'ok' && sorted.length > 0 && (
        <div className="pa-seg" role="tablist" aria-label="Planos mais recentes">
          {sorted.slice(0, 3).map(p => (
            <button key={p.id} type="button" role="tab" aria-selected={p.id === current?.id}
              title={p.plan_name} onClick={() => setSelId(p.id)}>
              {fmtDate(isoDay(p), { day: '2-digit', month: 'short' })}
              <small>{p.status === 'ativo' ? 'Ativo' : relDays(isoDay(p))}</small>
            </button>
          ))}
          <button type="button" className="pa-seg-all" onClick={onShowHistory} title="Abrir o histórico completo">
            <Ico e="🕓" />Todos
          </button>
        </div>
      )}
    </div>
  );

  /* ── estados de carregamento / erro / vazio ── */
  if (status === 'loading') {
    return (
      <div className="pa-root no-print" aria-busy="true">
        {ctx}
        <div className="pa-card pa-summary">
          <div className="pa-sum-left">
            <div className="pa-skel" style={{ width: '55%', height: 18, marginBottom: 14 }} />
            <div className="pa-skel" style={{ width: '40%', height: 52, marginBottom: 20 }} />
            <div className="pa-skel" style={{ width: '80%', height: 14 }} />
          </div>
          <div className="pa-sum-right">
            <div className="pa-skel" style={{ width: 148, height: 148, borderRadius: '50%' }} />
            <div style={{ flex: 1 }}>
              <div className="pa-skel" style={{ height: 14, marginBottom: 12 }} />
              <div className="pa-skel" style={{ height: 14, marginBottom: 12 }} />
              <div className="pa-skel" style={{ height: 14 }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="pa-root no-print">
        {ctx}
        <div className="pa-card pa-empty pa-fade">
          <div className="pa-empty-ico"><Ico e="⚠" size={24} /></div>
          <h3>Não foi possível carregar os planos</h3>
          <p>Verifique a conexão e tente de novo.</p>
          <div className="pa-empty-row">
            <button type="button" className="pa-btn pa-btn-ghost" onClick={onRetry}><Ico e="🔄" />Tentar novamente</button>
          </div>
        </div>
      </div>
    );
  }

  if (!current) {
    const first = (patient?.name || '').split(' ')[0];
    return (
      <div className="pa-root no-print">
        {ctx}
        <div className="pa-card pa-empty pa-fade">
          <div className="pa-empty-ico"><Ico e="📋" size={24} /></div>
          <h3>Ainda não há plano{first ? ` para ${first}` : ''}</h3>
          <p>Quando um plano for salvo, o resumo dele aparece aqui para consulta rápida.</p>
          <div className="pa-empty-row">
            <button type="button" className="pa-btn pa-btn-primary" onClick={onNew}><Ico e="✨" />Gerar com IA</button>
            <button type="button" className="pa-btn pa-btn-ghost" onClick={onManual}><Ico e="📝" />Plano manual</button>
            <button type="button" className="pa-btn pa-btn-ghost" onClick={onModelos}><Ico e="📋" />Usar modelo</button>
          </div>
        </div>
      </div>
    );
  }

  /* ── plano selecionado ── */
  const det      = details[current.id];
  const detail   = det?.status === 'ok' ? det.data : null;
  const kcal     = num(current.daily_calories);
  const macros   = macroInfo(current);
  const svc      = SERVICES.find(s => s.id === current.service_type);
  const strat    = strategyById(current.strategy);
  const water    = num(detail?.water_recommendation ?? current.water_recommendation);
  const meals    = detail ? parseMeals(detail.meals_plan) : [];
  const supsRaw  = detail ? (detail.supplements || detail.suplementacao || []) : [];
  const sups     = Array.isArray(supsRaw) ? supsRaw.filter(s => s && String(s.nome || '').trim()) : [];

  const macroRows = macros && [
    { k: 'prot', label: 'Proteína' },
    { k: 'carb', label: 'Carboidrato' },
    { k: 'fat',  label: 'Gordura' },
  ];

  return (
    <div className="pa-root no-print">
      {ctx}

      {/* ── Resumo ── */}
      <section className="pa-card pa-summary pa-fade" aria-label="Resumo do plano" key={current.id}>
        <div className="pa-sum-left">
          <div className="pa-plan-head">
            <div className="pa-plan-name">{current.plan_name || 'Plano sem nome'}</div>
            <button type="button" className="pa-btn pa-btn-ghost pa-btn-sm" onClick={() => onOpenEditor(current.id)}>
              <Ico e="✏" />Abrir no editor
            </button>
          </div>
          <div className="pa-chips">
            {current.status === 'ativo'
              ? <span className="pa-chip pa-chip-ok"><Ico e="✓" />Ativo</span>
              : current.status ? <span className="pa-chip">{capital(current.status)}</span> : null}
            {!!Number(current.ai_generated) && <span className="pa-chip pa-chip-ai"><Ico e="✨" />IA</span>}
            {svc && <span className="pa-chip">{svc.label}</span>}
            {strat && <span className="pa-chip">{strat.label}</span>}
          </div>

          {kcal > 0 ? (
            <div className="pa-kcal"><b>{fmtInt(kcal)}</b><span>kcal / dia</span></div>
          ) : (
            <div className="pa-ph"><Ico e="🔥" />Calorias totais não registradas neste plano.</div>
          )}

          <div className="pa-facts">
            <div>
              <div className="pa-fact-l">Início do plano</div>
              <div className="pa-fact-v"><Ico e="📅" />{fmtDate(String(current.start_date || '').slice(0, 10))}</div>
            </div>
            <div>
              <div className="pa-fact-l">Validade</div>
              <div className="pa-fact-v">{current.end_date ? fmtDate(String(current.end_date).slice(0, 10)) : '—'}</div>
            </div>
            <div>
              <div className="pa-fact-l">Água</div>
              <div className="pa-fact-v"><Ico e="💧" />{water > 0 ? `${String(water).replace('.', ',')} L` : '—'}</div>
            </div>
          </div>
        </div>

        <div className="pa-sum-right">
          {macros ? (
            <>
              <div className="pa-donut" role="img"
                aria-label={`Distribuição calórica: proteína ${macros.pct.prot}%, carboidrato ${macros.pct.carb}%, gordura ${macros.pct.fat}%`}>
                <PieChart width={148} height={148}>
                  <Pie data={macroRows.map(r => ({ name: r.label, value: macros.k[r.k], fill: colors[r.k] }))}
                    dataKey="value" cx="50%" cy="50%" innerRadius={54} outerRadius={70}
                    startAngle={90} endAngle={-270} paddingAngle={2} cornerRadius={4}
                    stroke="none" isAnimationActive={!prefersReducedMotion()} animationDuration={500}>
                    {macroRows.map(r => <Cell key={r.k} fill={colors[r.k]} />)}
                  </Pie>
                </PieChart>
                <div className="pa-donut-c">
                  <div><b>{macros.pct.prot}/{macros.pct.carb}/{macros.pct.fat}</b><small>P / C / G %</small></div>
                </div>
              </div>
              <div className="pa-macros">
                {macroRows.map(r => (
                  <div className="pa-mac" key={r.k}>
                    <span className="pa-dot" style={{ background: colors[r.k] }} />
                    <span className="pa-mac-n">{r.label}</span>
                    <span className="pa-mac-g">{fmtInt(macros.g[r.k])}g<small>{macros.pct[r.k]}%</small></span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="pa-ph" style={{ flex: 1 }}>
              <Ico e="🥧" />Macronutrientes não registrados neste plano. Abra no editor para calcular a partir dos alimentos.
            </div>
          )}
        </div>
      </section>

      {/* ── Refeições ── */}
      <div className="pa-sec-h">
        <h2>Refeições do dia</h2>
        {meals.length > 0 && <span className="pa-sec-meta">{meals.length} {meals.length > 1 ? 'refeições' : 'refeição'} · clique para ver porções</span>}
      </div>
      {!det || det.status === 'loading' ? (
        <div className="pa-card pa-meals" aria-busy="true">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="pa-meal" style={{ padding: '0.9rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <div className="pa-skel" style={{ width: 30, height: 30 }} />
              <div className="pa-skel" style={{ flex: 1, height: 14 }} />
            </div>
          ))}
        </div>
      ) : det.status === 'error' ? (
        <div className="pa-ph" style={{ marginBottom: '1.25rem', justifyContent: 'space-between' }}>
          <span>Não foi possível carregar as refeições deste plano.</span>
          <button type="button" className="pa-btn pa-btn-ghost pa-btn-sm" onClick={() => loadDetail(current.id)}><Ico e="🔄" />Tentar novamente</button>
        </div>
      ) : meals.length === 0 ? (
        <div className="pa-ph" style={{ marginBottom: '1.25rem' }}><Ico e="🍴" />Este plano não tem refeições registradas.</div>
      ) : (
        <div className="pa-card pa-meals pa-fade" key={`m-${current.id}`}>
          {meals.map(m => <MealRow key={m.key} meal={m} dayKcal={kcal} />)}
        </div>
      )}

      {/* ── Comparação + Suplementação ── */}
      <div className="pa-two">
        <ComparisonCard current={current} prev={prev} />
        <section className="pa-card pa-list" aria-label="Suplementação">
          <div className="pa-sec-h"><h2>Suplementação</h2></div>
          {det?.status === 'error' ? (
            <div className="pa-ph" role="status">Não foi possível carregar a suplementação. Use “Tentar novamente” acima para recarregar os detalhes do plano.</div>
          ) : !detail ? (
            <div className="pa-skel" aria-label="Carregando suplementação" style={{ height: 14 }} />
          ) : sups.length === 0 ? (
            <div className="pa-ph"><Ico e="💊" />Nenhum suplemento neste plano.</div>
          ) : sups.map((s, i) => (
            <div className="pa-li" key={i}>
              <span className="pa-li-sub" style={{ display: 'flex' }}><Ico e="💊" /></span>
              <div className="pa-li-grow">
                <b>{s.nome}</b>
                {[s.dosagem, s.momento_uso].filter(Boolean).length > 0 && (
                  <span className="pa-li-sub"> · {[s.dosagem, s.momento_uso].filter(Boolean).join(' · ')}</span>
                )}
              </div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

/* ── Linha de refeição (expansível) ── */
function MealRow({ meal, dayKcal }) {
  const [open, setOpen] = useState(false);
  const mk = meal.prot * 4 + meal.carb * 4 + meal.fat * 9;
  const id = `pa-meal-${meal.key}`;
  return (
    <div className="pa-meal">
      <button type="button" className="pa-meal-head" aria-expanded={open} aria-controls={id} onClick={() => setOpen(o => !o)}>
        <span className={`pa-m-time${meal.horario ? '' : ' pa-m-time-empty'}`}>{meal.horario || '—'}</span>
        <span className="pa-m-ico"><Ico e={mealIcon(meal.key)} /></span>
        <span className="pa-m-name">
          {meal.nome}
          <small>{meal.foods.length === 0 ? 'sem alimentos' : `${meal.foods.length} ${meal.foods.length > 1 ? 'itens' : 'item'}`}</small>
        </span>
        <span className="pa-m-foods">
          {meal.foods.slice(0, 3).map((f, i) => (
            <span className="pa-food" key={i} title={`${f.alimento}${f.porcao ? ' — ' + f.porcao : ''}`}>
              {f.alimento}{f.porcao ? <> <i>{f.porcao}</i></> : null}
            </span>
          ))}
          {meal.foods.length > 3 && <span className="pa-food pa-food-more">+{meal.foods.length - 3}</span>}
        </span>
        <span className="pa-m-kcal">
          {meal.hasCalories ? <><b>{fmtInt(meal.kcal)}</b> <small>kcal{dayKcal > 0 ? ` · ${Math.round(meal.kcal / dayKcal * 100)}%` : ''}</small></>
            : <small>Calorias não registradas</small>}
          {mk > 0 && (
            <span className="pa-bar" aria-hidden="true">
              <span style={{ width: `${meal.prot * 4 / mk * 100}%`, background: 'var(--pa-prot)' }} />
              <span style={{ width: `${meal.carb * 4 / mk * 100}%`, background: 'var(--pa-carb)' }} />
              <span style={{ width: `${meal.fat * 9 / mk * 100}%`, background: 'var(--pa-fat)' }} />
            </span>
          )}
        </span>
      </button>
      {open && (
        <div className="pa-meal-detail" id={id}>
          {meal.foods.length === 0 ? (
            <div className="pa-meal-obs">Nenhum alimento registrado nesta refeição.</div>
          ) : (
            <table>
              <tbody>
                {meal.foods.map((f, i) => (
                  <tr key={i}>
                    <td>{f.alimento}</td>
                    <td>{f.porcao || '—'}</td>
                    <td className="pa-num">{recorded(f.calorias) ? `${fmtInt(f.calorias)} kcal` : '—'}</td>
                    <td className="pa-num">P {nutrientText(f.proteina, 'g')}</td>
                    <td className="pa-num">C {nutrientText(f.carboidratos, 'g')}</td>
                    <td className="pa-num">G {nutrientText(f.gordura, 'g')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Comparado ao plano anterior (cor neutra: subir/descer depende do objetivo) ── */
function Delta({ cur, prev, unit }) {
  const d = Math.round(num(cur) - num(prev));
  if (!d) return <span className="pa-delta">= {unit}</span>;
  return (
    <span className="pa-delta">
      <Ico e={d > 0 ? '⬆' : '⬇'} />{d > 0 ? '+' : '−'}{Math.abs(d).toLocaleString('pt-BR')} {unit}
    </span>
  );
}

function ComparisonCard({ current, prev }) {
  if (!prev) {
    return (
      <section className="pa-card pa-list" aria-label="Comparação com o plano anterior">
        <div className="pa-sec-h"><h2>Comparado ao plano anterior</h2></div>
        <div className="pa-ph"><Ico e="ℹ" />Este é o plano mais antigo do paciente — não há anterior para comparar.</div>
      </section>
    );
  }
  const kc = num(current.daily_calories), kp = num(prev.daily_calories);
  const mc = macroInfo(current), mp = macroInfo(prev);
  return (
    <section className="pa-card pa-list" aria-label="Comparação com o plano anterior">
      <div className="pa-sec-h"><h2>Comparado ao plano anterior</h2></div>
      <div className="pa-cmp-sub">vs. {prev.plan_name || 'plano sem nome'} · {fmtDate(isoDay(prev))}</div>
      <div className="pa-li">
        <span className="pa-li-grow">Calorias</span>
        {kc > 0 && kp > 0
          ? <><b>{fmtInt(kc)} kcal</b><Delta cur={kc} prev={kp} unit="kcal" /></>
          : <span className="pa-li-sub">sem dados em um dos planos</span>}
      </div>
      {mc && mp ? (
        [['Proteína', 'prot'], ['Carboidrato', 'carb'], ['Gordura', 'fat']].map(([label, k]) => (
          <div className="pa-li" key={k}>
            <span className="pa-li-grow">{label}</span>
            <b>{fmtInt(mc.g[k])} g</b>
            <Delta cur={mc.g[k]} prev={mp.g[k]} unit="g" />
          </div>
        ))
      ) : (
        <div className="pa-li"><span className="pa-li-grow">Macronutrientes</span><span className="pa-li-sub">sem dados em um dos planos</span></div>
      )}
    </section>
  );
}
